import React from "react";
import { ChevronDown, UserPlus, X } from "lucide-react";
import { Button } from "../../atoms/Button/index.js";
import { IconTile } from "../../atoms/IconTile/index.js";
import { fieldFrameClass } from "../../lib/field-frame.js";
import { useFormField } from "../../lib/form-field-context.js";
import { cn } from "../../lib/utils.js";
import type { PeopleFieldChipView } from "./people-field-chip.js";
import type { PeopleFieldResolvedLabels } from "./people-field-labels.js";
import type { PeopleFieldSize } from "./people-field.types.js";
import { PeopleFieldValueRow } from "./PeopleFieldChip.js";

/* states-check-ignore: the frame is lib/field-frame's recipe, which paints
   the field's border, focus look and disabled dim; the clear control
   is the design-system Button. The body button only lays the row out. */

export interface PeopleFieldTriggerProps {
  /** The value as the field shows it; `null` when nobody is assigned. */
  readonly view: PeopleFieldChipView | null;
  /** The person's id, for their avatar colour. */
  readonly seed: string | undefined;
  readonly label: string;
  readonly emptyLabel: string;
  readonly labels: PeopleFieldResolvedLabels;
  readonly size: PeopleFieldSize;
  readonly open: boolean;
  readonly triggerId: string;
  /** The open list's id, for `aria-controls`. */
  readonly popupId: string;
  readonly readOnly: boolean;
  readonly disabled: boolean;
  readonly triggerRef: React.Ref<HTMLButtonElement>;
  readonly onToggle: () => void;
  /** ArrowDown or ArrowUp on the closed trigger. */
  readonly onOpenByKey: () => void;
  readonly onClear: () => void;
}

const TEXT: Record<PeopleFieldSize, string> = { sm: "text-xs", md: "text-sm" };

/**
 * The closed field, in the field frame every form control shares (Input,
 * Select): one row that never wraps. Empty, it reads "Assign person" beside a
 * person-plus mark. Filled, it shows the person (`PeopleFieldValueRow`), then
 * × to clear and the chevron. The frame is the `people-field` size container
 * the row's tag reads. Read-only, it is the read-only frame with no × and no
 * list (or a muted "Unassigned").
 */
export function PeopleFieldTrigger({
  view,
  seed,
  label,
  emptyLabel,
  labels,
  size,
  open,
  triggerId,
  popupId,
  readOnly,
  disabled,
  triggerRef,
  onToggle,
  onOpenByKey,
  onClear,
}: PeopleFieldTriggerProps): React.ReactElement {
  const field = useFormField();
  const tagId = React.useId();
  const describedBy =
    [field?.describedBy, view?.tag ? tagId : undefined]
      .filter(Boolean)
      .join(" ") || undefined;
  const frame = cn(
    fieldFrameClass({
      variant: readOnly ? "readonly" : "default",
      size,
      disabled,
    }),
    "@container/people-field w-full min-w-0 gap-1",
    size === "md" ? "ps-1.5 pe-1.5" : "ps-1 pe-1",
    TEXT[size],
  );

  if (readOnly) {
    // A static row has no name of its own, so the field's label is read first.
    return (
      <div className={cn(frame, "gap-2")}>
        <span className="sr-only">{`${label}: `}</span>
        {view ? (
          <PeopleFieldValueRow
            view={view}
            seed={seed}
            notActiveLabel={labels.notActive}
            tagId={tagId}
          />
        ) : (
          <span className="ps-1.5 text-pb-text-muted">{labels.unassigned}</span>
        )}
      </div>
    );
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    onOpenByKey();
  };
  return (
    <div className={frame}>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popupId : undefined}
        aria-label={
          view
            ? labels.changeName(label, view.name)
            : labels.openName(emptyLabel, label)
        }
        aria-describedby={describedBy}
        disabled={disabled}
        onClick={onToggle}
        onKeyDown={onKeyDown}
        className="flex h-full min-w-0 flex-1 items-center gap-2 text-start outline-none disabled:cursor-not-allowed"
      >
        {view ? (
          <PeopleFieldValueRow
            view={view}
            seed={seed}
            notActiveLabel={labels.notActive}
            tagId={tagId}
          />
        ) : (
          <>
            <IconTile
              size="xs"
              shape="circle"
              variant="outline"
              className="shrink-0"
            >
              <UserPlus />
            </IconTile>
            <span className="min-w-0 flex-1 truncate text-pb-text-muted">
              {emptyLabel}
            </span>
          </>
        )}
      </button>
      {view ? (
        <Button
          variant="ghost"
          size="xs"
          iconOnly
          aria-label={labels.removeName(label, view.name)}
          disabled={disabled}
          onClick={onClear}
          className="shrink-0 text-pb-text-muted"
        >
          <X className="size-3.5" />
        </Button>
      ) : null}
      {/* The chevron opens the list too: a label activates its button. */}
      <label
        htmlFor={triggerId}
        aria-hidden
        className={cn(
          "inline-flex shrink-0 px-0.5 text-pb-text-muted",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
        )}
      >
        <ChevronDown className="size-3.5" />
      </label>
    </div>
  );
}
